<template>
  <div class="bto-sb-search-result">
    <div class="bto-sb-search-wrapper">
      <div class="bto-sb-search-title">
        <h3 v-if="total > 0">{{ t("Mobile.SearchResults") }} - {{ total }}</h3>
        <h3 v-else>{{ t("Mobile.ResultsNotFound") }}</h3>
        <a :title="t('W.Clear')" @click.prevent="$emit('clearSearch')"></a>
      </div>
      <div v-bar="{ preventParentScroll: true }" class="bto-sb-scroll">
        <div>
          <div class="bto-sb-search-content">
            <div
              class="bto-sb-search-innercontent"
              v-for="(result, key) in groupedSearchResults"
              :key="key">
              <div class="bto-sb-search-innertitle">
                <span :class="['bto-sb-sports-icon', getSportClass(result)]"></span>
                <h3>{{ key }}</h3>
              </div>
              <router-link
                v-for="(match, index) in result"
                :key="index"
                :class="{ 'bto-sb-search-category': match.isTournament }"
                :to="openMatchDetails(match, getSportClass(result))"
                @click.native.prevent="$emit('clearSearch', true)">
                <span
                  v-bind:class="[
                    'bto-sb-search-time',
                    { 'bto-sb-search-time-live': match.matchType === 'L' },
                  ]">
                  {{ getMatchUpperText(match) }}
                </span>
                <span>{{ getMatchLowerText(match) }}</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import store from '@/store';
  import DateFilterService from '../../../../services/date-filter-service';
  import { urlFriendlyParam } from '@bto-services/utils';
  import { checkLayout } from 'bto-models/app-settings/web';

  export default {
    name: 'SearchResultsComponent',
    props: {
      searchEvents: {
        type: Array,
        default: () => [],
      },
      searchTournaments: {
        type: Array,
        default: () => [],
      },
    },
    computed: {
      /**
       * Get grouped search results by sport name
       */
      groupedSearchResults() {
        /**
         * Group search results by sportName
         * start by grouping the tournaments first,then the events
         */
        const groupedResults = this.searchTournaments
          .concat(this.searchEvents).reduce((gr, searchResult) => {
            const sName = searchResult.sportName.toLowerCase();
            if (typeof gr[sName] === 'undefined') {
              gr[sName] = [];
            }
            gr[sName].push(searchResult);
            return gr;
          }, {});
        /**
         * Order the grouped results by sport id
         */
        const orderedGroupedResults = Object.entries(groupedResults).sort((a, b) => {
          const aSid = (a[1] && a[1][0] && a[1][0].sportId) || 0;
          const bSid = (b[1] && b[1][0] && b[1][0].sportId) || 0;
          return aSid - bSid;
        }).reduce((acc, [key, val]) => {
          acc[key] = val;
          return acc;
        }, {});
        return orderedGroupedResults;
      },
      /**
       * Get total length of search results
       */
      total() {
        return this.searchEvents.length + this.searchTournaments.length;
      },
    },
    methods: {
      /**
       * Get sport class for given groupedResult
       */
      getSportClass(results) {
        const sportId = results[0] && results[0].sportId;
        return store.getters.getSportClass(sportId);
      },
      /**
       * Get prematch time for given match
       */
      getPreMatchTime(match) {
        let time = '';
        if (match.matchType === 'P') {
          time = ` - ${DateFilterService.localDate(
            match.matchDate,
            'dateTime',
            APP_SETTINGS.formatting.fullDateFormat,
            APP_SETTINGS.formatting.shortTimeFormat
          )}`;
        }
        return time;
      },
      /**
       * Get live match time for given match
       */
      getLiveMatchTime(match) {
        let time = '';
        if (match.matchType === 'L' && match.matchTime !== 0) {
          time = `${match.matchTime}' - `;
        }
        return time;
      },
      /**
       * Get search result text for first row
       */
      getMatchUpperText(match) {
        if (match.isTournament) {
          return match.tournamentName;
        }
        return `${this.getLiveMatchTime(match)}${match.matchName}`;
      },
      /**
       * Get search result text for second row
       */
      getMatchLowerText(match) {
        if (match.isTournament) {
          return match.categoryName;
        }
        const cName = match.categoryName || '';
        const tName = match.tournamentName || '';
        const dash = match.categoryName && match.tournamentName ? ' / ' : '';
        return `${cName}${dash}${tName}${this.getPreMatchTime(match)}`;
      },
      /**
       * Handle click on search result
       */
      openMatchDetails(match, sportClass) {
        if (match.isTournament) {
          const fixtureIds = `${match.tournamentId}`;
          if (sportClass === 'horse-racing' || sportClass === 'greyhound') {
            const appendedFixture = `${sportClass === 'greyhound' ? 'gh' : 'hr'}${fixtureIds}`;
            return {
              name: 'sports', params: { fixtureIds: appendedFixture, fixtureDateFilter: 'all' }
            };
          }
          if (checkLayout('US')) {
            const sportName = urlFriendlyParam(match.sportName);
            const tournamentName = urlFriendlyParam(match.tournamentName);
            return { name: 'seoTournament', params: { fixtureIds, sport: sportName, tournament: tournamentName } };
          }
          return { name: 'sports', params: { fixtureIds, fixtureDateFilter: 'all' } };
        }
        const sportName = urlFriendlyParam(match.sportName);
        const categoryName = urlFriendlyParam(match.tournamentName);
        const eventName = urlFriendlyParam(match.matchName);
        if (match.matchType.toLowerCase() === 'l') {
          return { name: 'seoGameLive', params: { sportName, categoryName, eventName, matchId: match.betradarId } };
        }
        return { name: 'seoGame', params: { sportName, categoryName, eventName, matchId: match.matchId } };
      },
    },
  };
</script>
